import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { sendAnonymousMessage, streamMessage } from '../../services/api';
import { waitForClerkToken } from '../../services/clerkToken';
import SiriGlowOrb from './SiriGlowOrb';
import './VoiceConversation.css';

const getApiBaseUrl = () => {
  const configuredUrl = (process.env.REACT_APP_API_URL || '').trim();
  if (configuredUrl && configuredUrl !== 'undefined') return configuredUrl.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'http://localhost:8000';
};

const cleanForSpeech = (text = '') => String(text)
  .replace(/```[\s\S]*?```/g, 'code example omitted in voice')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/__([^_]+)__/g, '$1')
  .replace(/\*([^*]+)\*/g, '$1')
  .replace(/_([^_]+)_/g, '$1')
  .replace(/[*#>`~]/g, '')
  .replace(/\.{2,}/g, '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(/\s+/g, ' ')
  .trim();

const getPreferredVoice = (voices) => {
  if (!voices?.length) return null;

  const preferredPatterns = [
    /microsoft.*(sonia|ryan|libby|george|uk|united kingdom|great britain)/i,
    /(sonia|ryan|libby|george).*microsoft/i,
    /en-GB/i,
    /english.*united kingdom/i,
  ];

  for (const pattern of preferredPatterns) {
    const voice = voices.find(v => pattern.test(`${v.name} ${v.lang}`));
    if (voice) return voice;
  }

  return voices.find(v => /^en/i.test(v.lang)) || voices[0];
};

const VoiceConversation = ({ currentChat, isAuthenticated, onLocationDetected, userName }) => {
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [statusText, setStatusText] = useState('Tap the orb to speak');
  const [messages, setMessages] = useState([]);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [micAnalyser, setMicAnalyser] = useState(null);
  const [voiceConsentAccepted, setVoiceConsentAccepted] = useState(() => {
    try {
      return localStorage.getItem('tulizaVoiceConsentAccepted') === 'true';
    } catch {
      return false;
    }
  });

  const isOpenRef = useRef(false);
  const phaseRef = useRef('idle');
  const audioEnabledRef = useRef(true);
  const voiceConsentAcceptedRef = useRef(voiceConsentAccepted);
  const messagesRef = useRef([]);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const speechRef = useRef(null);
  const transcriptRef = useRef(null);
  const currentChatRef = useRef(currentChat);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const onLocationDetectedRef = useRef(onLocationDetected);
  const userNameRef = useRef(userName);
  const discardRecordingRef = useRef(false);
  const sessionTokenRef = useRef(0);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { audioEnabledRef.current = audioEnabled; }, [audioEnabled]);
  useEffect(() => { voiceConsentAcceptedRef.current = voiceConsentAccepted; }, [voiceConsentAccepted]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { currentChatRef.current = currentChat; }, [currentChat]);
  useEffect(() => { isAuthenticatedRef.current = isAuthenticated; }, [isAuthenticated]);
  useEffect(() => { onLocationDetectedRef.current = onLocationDetected; }, [onLocationDetected]);
  useEffect(() => { userNameRef.current = userName; }, [userName]);
  useEffect(() => { transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, statusText]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return undefined;
    window.speechSynthesis.getVoices();
    const onVoicesChanged = () => window.speechSynthesis.getVoices();
    window.speechSynthesis.addEventListener?.('voiceschanged', onVoicesChanged);
    return () => window.speechSynthesis.removeEventListener?.('voiceschanged', onVoicesChanged);
  }, []);

  const stopListeningLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const stopSpeaking = useCallback(() => {
    try { window.speechSynthesis?.cancel(); } catch {}
    speechRef.current = null;
  }, []);

  const stopMic = useCallback((discardRecording = true) => {
    stopListeningLoop();
    try {
      if (recorderRef.current?.state === 'recording') {
        discardRecordingRef.current = discardRecording;
        recorderRef.current.stop();
      }
    } catch {}
    recorderRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    try { audioCtxRef.current?.close(); } catch {}
    audioCtxRef.current = null;
    analyserRef.current = null;
    setMicAnalyser(null);
  }, []);

  const teardown = useCallback(() => {
    sessionTokenRef.current += 1;
    stopSpeaking();
    stopMic(true);
    setPhase('idle');
    setStatusText('Tap the orb to speak');
  }, [stopMic, stopSpeaking]);

  useEffect(() => () => teardown(), [teardown]);

  const getRMS = () => {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    const buffer = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteTimeDomainData(buffer);
    let sum = 0;
    for (let i = 0; i < buffer.length; i += 1) {
      const value = buffer[i] - 128;
      sum += value * value;
    }
    return Math.sqrt(sum / buffer.length);
  };

  const openMic = async () => {
    if (streamRef.current && audioCtxRef.current && analyserRef.current) return true;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: 48000,
      },
    });
    streamRef.current = stream;

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContextCtor();
    audioCtxRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.72;
    source.connect(analyser);
    analyserRef.current = analyser;
    setMicAnalyser(analyser);
    return true;
  };

  const transcribeBlob = async (audioBlob, mimeType) => {
    if (!audioBlob || audioBlob.size < 300) return '';

    const formData = new FormData();
    const extension = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('ogg') ? 'ogg' : 'webm';
    formData.append('audio', audioBlob, `voice-panel.${extension}`);
    formData.append('language', 'auto');
    formData.append('context_type', 'healthcare');

    const headers = {};
    try {
      const token = await waitForClerkToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {}

    const response = await fetch(`${getApiBaseUrl()}/api/chatbot/chats/transcribe_voice/`, {
      method: 'POST',
      headers,
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Voice transcription failed (${response.status})`);
    return data.success ? (data.text || '').trim() : '';
  };

  const speak = useCallback((text, onComplete) => {
    const token = sessionTokenRef.current;
    if (!audioEnabledRef.current || !window.speechSynthesis) {
      if (isOpenRef.current && token === sessionTokenRef.current) onComplete?.();
      return;
    }

    stopSpeaking();
    const utterance = new SpeechSynthesisUtterance(cleanForSpeech(text));
    const voices = window.speechSynthesis.getVoices();
    const voice = getPreferredVoice(voices);
    if (voice) utterance.voice = voice;
    utterance.lang = voice?.lang || 'en-GB';
    utterance.rate = 1.08;
    utterance.pitch = 1;

    utterance.onstart = () => {
      if (!isOpenRef.current || token !== sessionTokenRef.current) return;
      setPhase('speaking');
      setStatusText(voice?.name?.toLowerCase().includes('microsoft') ? 'Speaking with Microsoft UK voice...' : 'Speaking...');
      startBargeInLoop();
    };
    utterance.onend = () => {
      if (!isOpenRef.current || token !== sessionTokenRef.current) return;
      stopMic(true);
      setPhase('idle');
      setStatusText('Listening...');
      if (onComplete) {
        onComplete();
      } else {
        setTimeout(() => {
          if (isOpenRef.current && phaseRef.current === 'idle') {
            beginListening();
          }
        }, 80);
      }
    };
    utterance.onerror = () => {
      if (!isOpenRef.current || token !== sessionTokenRef.current) return;
      stopMic(true);
      onComplete?.();
    };

    speechRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [stopMic, stopSpeaking]);

  const beginListening = useCallback(async () => {
    if (!isOpenRef.current || !voiceConsentAcceptedRef.current || phaseRef.current === 'listening') return;
    const token = sessionTokenRef.current;
    stopSpeaking();
    stopMic(true);

    try {
      await openMic();
      if (!isOpenRef.current || token !== sessionTokenRef.current) {
        stopMic(true);
        return;
      }
    } catch {
      setPhase('idle');
      setStatusText('Microphone permission is needed');
      return;
    }

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

    chunksRef.current = [];
    discardRecordingRef.current = false;
    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    recorderRef.current = recorder;
    recorder.ondataavailable = event => {
      if (event.data?.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => finishRecording(mimeType);

    recorder.start(160);
    setPhase('listening');
    setStatusText('Listening...');

    const startedAt = Date.now();
    const SPEECH_THRESHOLD = 2.5;
    const SILENCE_AFTER_SPEECH_MS = 220;
    const NO_SPEECH_TIMEOUT_MS = 1200;
    const MAX_RECORDING_MS = 15000;
    const MIN_RECORDING_MS = 400;
    let heardSpeech = false;
    let quietSince = null;

    const tick = () => {
      if (phaseRef.current !== 'listening' || token !== sessionTokenRef.current) return;
      const rms = getRMS();
      const now = Date.now();

      if (rms > SPEECH_THRESHOLD) {
        heardSpeech = true;
        quietSince = null;
      } else if (heardSpeech && quietSince === null) {
        quietSince = now;
      }

      const pastMinimum = now - startedAt > MIN_RECORDING_MS;
      const speechEnded = heardSpeech && quietSince && now - quietSince > SILENCE_AFTER_SPEECH_MS && pastMinimum;
      const noSpeechTimeout = !heardSpeech && now - startedAt > NO_SPEECH_TIMEOUT_MS;
      const hardStop = now - startedAt > MAX_RECORDING_MS;

      if (speechEnded || noSpeechTimeout || hardStop) {
        try {
          discardRecordingRef.current = false;
          recorderRef.current?.state === 'recording' && recorderRef.current.stop();
        } catch {}
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [stopMic, stopSpeaking]);

  const stopActiveRecording = useCallback(() => {
    if (phaseRef.current !== 'listening') return;
    try {
      discardRecordingRef.current = false;
      recorderRef.current?.state === 'recording' && recorderRef.current.stop();
    } catch {}
  }, []);

  const excuseAndListenAgain = useCallback((message = "I'm sorry, I didn't catch that clearly. Please try again, or use the text box on the right.") => {
    if (!isOpenRef.current) return;
    setMessages(prev => [...prev, { role: 'assistant', content: message, timestamp: new Date() }]);
    setStatusText('Please try again');
    if (audioEnabledRef.current) {
      speak(message, () => beginListening());
    } else {
      window.setTimeout(() => {
        if (isOpenRef.current) beginListening();
      }, 700);
    }
  }, [beginListening, speak]);

  const processText = useCallback(async (text) => {
    if (!isOpenRef.current) return;
    const trimmed = text.trim();
    if (!trimmed) {
      excuseAndListenAgain();
      return;
    }

    const userMessage = { role: 'user', content: trimmed, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setPhase('processing');
    setStatusText('Thinking...');

    const panelHistory = [...messagesRef.current, userMessage]
      .slice(-12)
      .map(msg => ({
        role: msg.role === 'assistant' ? 'assistant' : 'user',
        content: msg.content,
      }));

    try {
      let assistantText = '';
      const activeChat = currentChatRef.current;

      if (isAuthenticatedRef.current && activeChat?.url_code) {
        let streamError = null;
        await streamMessage(
          trimmed,
          'auto',
          panelHistory,
          activeChat.url_code,
          chunk => {
            assistantText += chunk;
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant' && last.streaming) {
                return [...prev.slice(0, -1), { ...last, content: assistantText }];
              }
              return [...prev, { role: 'assistant', content: assistantText, timestamp: new Date(), streaming: true }];
            });
          },
          () => {},
          error => { streamError = error || 'Voice response failed'; }
        );
        if (streamError) throw new Error(streamError);
      } else {
        const response = await sendAnonymousMessage(trimmed, 'auto', 'flash', panelHistory);
        assistantText = response?.assistant_message?.content || '';
        setMessages(prev => [...prev, { role: 'assistant', content: assistantText, timestamp: new Date() }]);
      }

      if (!isOpenRef.current) return;

      const finalText = assistantText.trim() || "I'm sorry, I couldn't form a clear answer. Could you say that again?";
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.streaming) {
          return [...prev.slice(0, -1), { ...last, content: finalText, streaming: false }];
        }
        return prev;
      });

      onLocationDetectedRef.current?.(trimmed, finalText);
      speak(finalText, () => beginListening());
    } catch (error) {
      if (!isOpenRef.current) return;
      const fallback = "I'm sorry, voice chat had trouble reaching Tuliza. Please try again in a moment.";
      setMessages(prev => [...prev, { role: 'assistant', content: fallback, timestamp: new Date() }]);
      setStatusText(error.message || 'Voice chat failed');
      speak(fallback, () => setPhase('idle'));
    }
  }, [beginListening, excuseAndListenAgain, speak]);

  const finishRecording = async (mimeType) => {
    stopListeningLoop();
    if (discardRecordingRef.current || !isOpenRef.current) {
      discardRecordingRef.current = false;
      return;
    }

    setPhase('processing');
    setStatusText('Understanding...');

    const blob = new Blob(chunksRef.current, { type: mimeType });
    stopMic(true);

    try {
      const text = await transcribeBlob(blob, mimeType);
      if (!text) {
        excuseAndListenAgain();
        return;
      }
      processText(text);
    } catch (error) {
      setStatusText(error.message || 'Voice transcription failed');
      excuseAndListenAgain("I'm sorry, I couldn't understand the audio clearly. Please try once more.");
    }
  };

  const startBargeInLoop = useCallback(() => {
    stopListeningLoop();
    openMic().then(() => {
      if (!isOpenRef.current) stopMic(true);
    }).catch(() => false);

    let loudFrames = 0;
    const startedAt = Date.now();
    const token = sessionTokenRef.current;

    const tick = () => {
      if (phaseRef.current !== 'speaking' || !isOpenRef.current || token !== sessionTokenRef.current) return;
      if (Date.now() - startedAt < 120) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (getRMS() > 7) {
        loudFrames += 1;
        if (loudFrames >= 1) {
          stopSpeaking();
          setStatusText('I heard you. Go ahead.');
          beginListening();
          return;
        }
      } else {
        loudFrames = Math.max(0, loudFrames - 1);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [beginListening, stopMic, stopSpeaking]);

  const startVoiceSession = useCallback(() => {
    if (!isOpenRef.current) return;
    const firstName = (userNameRef.current || '').trim().split(/\s+/)[0];
    const welcome = firstName
      ? `Hi ${firstName}, I'm Tuliza voice. You can speak naturally, and I'll keep a written transcript on the right.`
      : "Hi, I'm Tuliza voice. You can speak naturally, and I'll keep a written transcript on the right.";

    setMessages(prev => prev.length ? prev : [{ role: 'assistant', content: welcome, timestamp: new Date() }]);
    window.setTimeout(() => {
      if (isOpenRef.current) {
        speak(welcome, () => beginListening());
      }
    }, 100);
  }, [beginListening, speak]);

  const openPanel = useCallback(() => {
    if (isOpenRef.current) return;
    sessionTokenRef.current += 1;
    setIsOpen(true);
    isOpenRef.current = true;

    if (voiceConsentAcceptedRef.current) {
      window.setTimeout(() => {
        if (isOpenRef.current) {
          startVoiceSession();
        }
      }, 150);
    } else {
      setPhase('idle');
      setStatusText('Voice consent needed');
    }
  }, [startVoiceSession]);

  useEffect(() => {
    const openHandler = () => openPanel();
    window.addEventListener('open-voice-conversation', openHandler);
    return () => window.removeEventListener('open-voice-conversation', openHandler);
  }, [openPanel]);

  const closePanel = () => {
    isOpenRef.current = false;
    teardown();
    setIsOpen(false);
  };

  const acceptVoiceConsent = () => {
    try {
      localStorage.setItem('tulizaVoiceConsentAccepted', 'true');
    } catch {}
    setVoiceConsentAccepted(true);
    voiceConsentAcceptedRef.current = true;
    startVoiceSession();
  };

  const statusLabel = useMemo(() => {
    if (phase === 'listening') return 'Listening';
    if (phase === 'processing') return 'Thinking';
    if (phase === 'speaking') return 'Speaking';
    return statusText;
  }, [phase, statusText]);

  const panel = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="voice-panel-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => { if (e.target === e.currentTarget) closePanel(); }}
        >
          <motion.section
            className="voice-panel"
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 24 }}
            transition={{ type: 'spring', stiffness: 310, damping: 28 }}
            aria-label="Tuliza voice conversation"
          >
            <div className="voice-panel-main">
              <div className="voice-panel-top">
                <div>
                  <h2>Tuliza Voice</h2>
                  <p>Speak in any language. I'll ask again if I don't understand.</p>
                </div>
                <div className="voice-panel-actions">
                  <button className="voice-panel-icon-btn" onClick={() => setAudioEnabled(prev => !prev)} title={audioEnabled ? 'Mute voice replies' : 'Unmute voice replies'}>
                    {audioEnabled ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H3v6h3l5 4V5zM16 9a5 5 0 0 1 0 6M19 6a9 9 0 0 1 0 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H3v6h3l5 4V5zM23 9l-6 6M17 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                  </button>
                  <button className="voice-panel-icon-btn" onClick={closePanel} title="Close voice chat">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>
                  </button>
                </div>
              </div>

              <button
                className={`voice-orb-button ${phase}`}
                onClick={() => {
                  if (!voiceConsentAccepted) return;
                  if (phase === 'listening') stopActiveRecording();
                  else beginListening();
                }}
                disabled={phase === 'processing'}
                title={phase === 'listening' ? 'Stop and process' : phase === 'speaking' ? 'Interrupt and speak' : 'Start speaking'}
              >
                <SiriGlowOrb phase={phase} size={236} analyser={phase === 'listening' ? micAnalyser : null} />
                <span className="voice-orb-center">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor" strokeWidth="2" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              </button>

              <div className={`voice-status-pill ${phase}`}>
                <span />
                {statusLabel}
              </div>
              <p className="voice-status-detail">{statusText}</p>

              {!voiceConsentAccepted && (
                <div className="voice-consent-card">
                  <h3>Before voice starts</h3>
                  <p>
                    Voice conversations are transcribed and chat replies are saved automatically with this conversation.
                    Do not share passport numbers, Home Office references, or sensitive IDs.
                  </p>
                  <div className="voice-consent-actions">
                    <button className="voice-consent-secondary" onClick={closePanel}>Not now</button>
                    <button className="voice-consent-primary" onClick={acceptVoiceConsent}>I understand</button>
                  </div>
                </div>
              )}
            </div>

            <aside className="voice-transcript">
              <div className="voice-transcript-header">
                <span>Live conversation</span>
                <button onClick={() => setMessages([])}>Clear</button>
              </div>
              <div className="voice-transcript-list" ref={transcriptRef}>
                {messages.map((message, index) => (
                  <div key={`${message.role}-${index}`} className={`voice-transcript-message ${message.role}`}>
                    <span>{message.role === 'user' ? 'You' : 'Tuliza'}</span>
                    <p>{message.content}</p>
                  </div>
                ))}
              </div>
            </aside>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return mounted ? createPortal(panel, document.body) : null;
};

export default VoiceConversation;
